import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { toast } from 'sonner';
import { FileText, Plus, Clock, CheckCircle, XCircle, AlertTriangle, Filter, ChevronDown, Eye } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import api from '../services/api';

const RequestsPage = () => {
  const { user } = useAuth();
  const { userTheme } = useTheme();
  const [requests, setRequests] = useState([]);
  const [userStats, setUserStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');

  // Get theme values
  const pageStyles = userTheme?.page || {};
  const requestsBox = userTheme?.requestsBox || {};
  const buttonStyles = userTheme?.buttons || {};
  const primaryColor = buttonStyles.primaryColor || '#2B4B6A';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [requestsRes, statsRes] = await Promise.all([
        api.get('/quote-process/requests'),
        api.get('/user/stats').catch(() => ({ data: {} }))
      ]);
      setRequests(requestsRes.data);
      setUserStats(statsRes.data);
    } catch (error) {
      toast.error('Failed to load requests');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: { bg: requestsBox.statusPendingColor || '#FEF3C7', text: '#92400E', icon: Clock },
      approved: { bg: requestsBox.statusApprovedColor || '#D1FAE5', text: '#065F46', icon: CheckCircle },
      denied: { bg: requestsBox.statusRejectedColor || '#FEE2E2', text: '#991B1B', icon: XCircle },
      more_info: { bg: '#DBEAFE', text: '#1E40AF', icon: AlertTriangle }
    };
    const style = styles[status] || styles.pending;
    const Icon = style.icon;
    return (
      <span 
        className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium"
        style={{ backgroundColor: style.bg, color: style.text }}
      >
        <Icon className="w-3 h-3" />
        {status.replace('_', ' ')}
      </span>
    );
  };

  // Check if user can submit LOI based on new tier system
  const canSubmitLOI = userStats?.can_submit_loi || 
    (userStats?.tier && ['silver', 'gold'].includes(userStats.tier) && userStats?.tier_status === 'approved');

  if (loading) {
    return (
      <Sidebar>
        <div 
          className="min-h-screen flex items-center justify-center"
          style={{ backgroundColor: pageStyles.backgroundColor || '#F9FAFB' }}
        >
          <div 
            className="animate-spin rounded-full h-12 w-12 border-2 border-gray-300"
            style={{ borderTopColor: primaryColor }}
          ></div>
        </div>
      </Sidebar>
    );
  }

  // Filter requests by status
  const filteredRequests = statusFilter === 'all' 
    ? requests 
    : requests.filter(r => r.status === statusFilter);

  return (
    <Sidebar>
      <div 
        className="p-8 min-h-screen"
        style={{ backgroundColor: pageStyles.backgroundColor || '#F9FAFB' }}
      >
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 
              className="text-2xl font-semibold mb-2"
              style={{ color: requestsBox.titleColor || '#111827' }}
            >
              My Quote Requests
            </h1>
            <p style={{ color: requestsBox.textColor || '#6B7280' }}>
              View and manage your quote submissions
            </p>
          </div>
          {canSubmitLOI && (
            <Link
              to="/admin/requests/new"
              className="text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2 hover:opacity-90 transition"
              style={{ backgroundColor: primaryColor }}
            >
              <Plus className="w-5 h-5" />
              New Request
            </Link>
          )}
        </div>

        {/* Status Filter */}
        {requests.length > 0 && (
          <div className="mb-6 flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4" style={{ color: requestsBox.textColor || '#6B7280' }} />
              <span className="text-sm font-medium" style={{ color: requestsBox.textColor || '#6B7280' }}>Filter:</span>
            </div>
            <div className="relative">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="appearance-none pl-3 pr-8 py-2 border rounded-lg text-sm bg-white focus:outline-none focus:ring-2"
                style={{ borderColor: requestsBox.borderColor || '#E5E7EB' }}
                data-testid="status-filter"
              >
                <option value="all">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="denied">Denied</option>
                <option value="more_info">More Info Needed</option>
              </select>
              <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
            <span className="text-sm" style={{ color: requestsBox.textColor || '#6B7280' }}>
              {filteredRequests.length} request{filteredRequests.length !== 1 ? 's' : ''}
            </span>
          </div>
        )}

        {requests.length === 0 ? (
          <div 
            className="rounded-xl p-12 text-center"
            style={{ 
              backgroundColor: requestsBox.backgroundColor || '#FFFFFF',
              border: `1px solid ${requestsBox.borderColor || '#E5E7EB'}`
            }}
          >
            <FileText className="w-16 h-16 mx-auto mb-4" style={{ color: requestsBox.borderColor || '#D1D5DB' }} />
            <h3 className="text-lg font-medium mb-2" style={{ color: requestsBox.titleColor || '#4B5563' }}>
              No requests yet
            </h3>
            <p className="mb-4" style={{ color: requestsBox.textColor || '#6B7280' }}>
              {canSubmitLOI 
                ? "Start your first quote request to get started."
                : "Complete Silver verification to request quotes."}
            </p>
            {canSubmitLOI && (
              <Link
                to="/admin/requests/new"
                className="inline-flex items-center gap-2 text-white px-6 py-2 rounded-lg font-medium hover:opacity-90 transition"
                style={{ backgroundColor: primaryColor }}
              >
                <Plus className="w-5 h-5" />
                Start First Request
              </Link>
            )}
          </div>
        ) : filteredRequests.length === 0 ? (
          <div 
            className="rounded-xl p-12 text-center"
            style={{ 
              backgroundColor: requestsBox.backgroundColor || '#FFFFFF',
              border: `1px solid ${requestsBox.borderColor || '#E5E7EB'}`
            }}
          >
            <FileText className="w-16 h-16 mx-auto mb-4" style={{ color: requestsBox.borderColor || '#D1D5DB' }} />
            <h3 className="text-lg font-medium mb-2" style={{ color: requestsBox.titleColor || '#4B5563' }}>
              No requests match this filter
            </h3>
            <p style={{ color: requestsBox.textColor || '#6B7280' }}>
              Try selecting a different status filter.
            </p>
          </div>
        ) : (
          <div 
            className="rounded-xl shadow-sm overflow-hidden"
            style={{ 
              backgroundColor: requestsBox.backgroundColor || '#FFFFFF',
              border: `1px solid ${requestsBox.borderColor || '#E5E7EB'}`
            }}
          >
            <table className="w-full">
              <thead style={{ backgroundColor: pageStyles.backgroundColor || '#F9FAFB', borderBottom: `1px solid ${requestsBox.borderColor || '#E5E7EB'}` }}>
                <tr>
                  <th className="text-left px-6 py-3 text-sm font-medium" style={{ color: requestsBox.textColor || '#6B7280' }}>Request ID</th>
                  <th className="text-left px-6 py-3 text-sm font-medium" style={{ color: requestsBox.textColor || '#6B7280' }}>Product</th>
                  <th className="text-left px-6 py-3 text-sm font-medium" style={{ color: requestsBox.textColor || '#6B7280' }}>Current Step</th>
                  <th className="text-left px-6 py-3 text-sm font-medium" style={{ color: requestsBox.textColor || '#6B7280' }}>Status</th>
                  <th className="text-right px-6 py-3 text-sm font-medium" style={{ color: requestsBox.textColor || '#6B7280' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredRequests.map((request, idx) => (
                  <tr 
                    key={request.id} 
                    className="hover:bg-gray-50"
                    style={{ borderTop: idx > 0 ? `1px solid ${requestsBox.borderColor || '#E5E7EB'}` : 'none' }}
                  >
                    <td className="px-6 py-4">
                      <span className="font-mono text-sm" style={{ color: requestsBox.titleColor || '#111827' }}>
                        #{request.id?.slice(-8)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm font-medium" style={{ color: requestsBox.titleColor || '#111827' }}>
                        {request.product_name || '-'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <span 
                          className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium"
                          style={{ backgroundColor: `${primaryColor}15`, color: primaryColor }}
                        >
                          Step {request.current_step || 1}
                        </span>
                        <span className="text-xs" style={{ color: requestsBox.textColor || '#6B7280' }}>
                          ({request.progress})
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {getStatusBadge(request.status)}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <Link
                        to={`/admin/requests/${request.id}`}
                        className="inline-flex items-center justify-center w-8 h-8 rounded-lg hover:bg-gray-100 transition"
                        style={{ color: primaryColor }}
                        title="View Details"
                      >
                        <Eye className="w-5 h-5" />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Sidebar>
  );
};

export default RequestsPage;
